/** Coach (P8): day state, edge map, mentor, playbook, academy, the path to funded; admin flags. The guard codes stay for the terminal. */
export default {
  nav: "Coach",
  title: "Coach",
  lead: "Where your results come from, a mentor who reads your trades, and the path to a funded account.",
  account: "Account",
  tabs: { analytics: "Edge map", mentor: "Mentor", playbook: "Playbook", academy: "Academy", path: "Path to funded" },
  day: { warning: "Careful — close to your limit", locked: "Locked for today", reasons: { green_to_red: "the day gave back too much from its peak", personal_daily_loss: "your own daily loss limit" } },
  guard: {
    codes: {
      COACH_PAUSE_AFTER_LOSS: "pause after a loss",
      COACH_REENTRY_LIMIT: "re-entry limit",
      COACH_TRADE_LIMIT: "trades per day",
      COACH_GREEN_TO_RED: "green to red",
      COACH_OUTSIDE_WINDOW: "outside your window",
      COACH_PERSONAL_DAILY_LOSS: "your daily loss",
    },
  },
  analytics: {
    dimension: "Group by",
    dims: { instrument: "Instrument", side: "Side", hour: "Hour", weekday: "Weekday", holding: "Holding time", setup: "Setup" },
    trades: "Trades",
    net: "Net",
    winRate: "Win rate",
    pf: "Profit factor",
    payoff: "Payoff",
    dd: "Max drawdown",
    netTotal: "Net result, all closed trades",
    rowLine: "{trades} trades · win {win} · PF {pf}",
    emptyTitle: "No closed trades yet",
    empty: "Close a few trades and the map shows which instruments, hours and setups make your results.",
    toTerminal: "Open the terminal",
    zone: "Times in {tz}",
  },
  mentor: {
    intro: "The mentor reads your trades, rules and limits — never market tips. Answers are coaching, not investment advice.",
    brief: "Brief before the session",
    debrief: "Debrief after the session",
    weekly: "Weekly review",
    off: "The mentor is not enabled for your account yet.",
    thinking: "The mentor is reading your trades…",
    none: "No sessions yet. Ask for a brief before you trade, or a debrief after.",
    memory: "Memory",
    memoryLead: "What the mentor keeps in mind: your commitments, notes and facts. Tick a commitment when it is done.",
    kind: "Type",
    text: "Text",
    placeholders: { commitment: "e.g. No trades in the first 15 minutes", note: "e.g. Sleep was short today", fact: "e.g. I trade best on the London open" },
    add: "Add",
    kinds: { note: "Note", fact: "Fact", commitment: "Commitment" },
  },
  playbook: {
    new: "New setup",
    intro: "Name your setups; tag closed trades with them to see each setup's results in the edge map.",
    name: "Setup name",
    tag: "Tag",
    tagHint: "Lower-case letters, digits and dashes; filled from the name.",
    rules: "Entry and exit rules",
    add: "Save setup",
    saved: "Setup saved.",
    list: "Your setups",
    none: "No setups yet. The ones you save appear here.",
    tagTrade: "Setup",
    followed: "Followed the plan",
  },
  academy: {
    title: "Lessons",
    progress: "{done} of {total} read",
    done: "Mark as read",
    completed: "Read",
    lessons: {
      "risk-of-ruin": { title: "Risk of ruin", body: "Risking 1 % per trade, ten losses in a row cost about 10 %. Risking 5 %, they cost 40 %. Size every trade from its stop, not from how sure you feel." },
      "daily-loss": { title: "The daily loss limit", body: "The limit is measured on equity, including open losses, from the program's day boundary. Leave room: stop well before it, not at it." },
      "position-sizing": { title: "Position sizing", body: "Quantity = risk in $ ÷ (distance to stop × value of one point). The order ticket does this for you with “size from risk”." },
      tilt: { title: "Tilt", body: "After a loss the urge to win it back is strongest. Step away for a few minutes, then size the next trade from its stop, not from the loss." },
      journaling: { title: "Journaling", body: "Tag each trade with its setup and whether you followed the plan. In a month the edge map shows what to keep and what to drop." },
      "execution-policy": { title: "How fills work here", body: "Orders fill instantly, in 10–30 ms with no added latency, walking the real book; limits queue behind visible size. Each fill keeps a snapshot — open “Why here” in the blotter." },
    },
  },
  path: {
    intro: "Your path, step by step.",
    steps: { challenge: "Start a challenge", first_trade: "First trade", phase_passed: "Pass a phase", kyc: "Verify your identity", funded: "Funded account", first_payout: "First payout" },
    actions: { challenge: "Open an account", first_trade: "Open the terminal", phase_passed: "Open the terminal", kyc: "Verify now", funded: "See your accounts", first_payout: "Go to payouts" },
    badges: "Badges",
    earned: "{n} of {total}",
    achievements: {
      first_trade: "First trade",
      ten_trades: "Ten trades",
      green_day: "Green day",
      plan_followed_5: "Followed the plan 5 times",
      academy_done: "Academy done",
      phase_passed: "Phase passed",
      funded: "Funded",
      first_payout: "First payout",
    },
  },
  admin: {
    tab: "Experiments",
    flag: "Flag",
    enabled: "On",
    rollout: "Treatment %",
    save: "Save",
    variant: "Variant",
    users: "Users",
    pF: "Pass rate p_f",
    q: "Paid funded q",
    dll: "DLL breaches",
    retention: "7-day retention",
  },
} as const;
